import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output } from '@angular/core';
import type { MapsCandidate } from '../../models/maps.types';
import {
    ATTRIBUTE_LABELS,
    ComputeFacetCounts,
    CountActiveFilters,
    EmptyFilterState,
    type FacetCounts,
    type MapsFilterAttribute,
    type MapsFilterState,
} from '../../utils/filtering';
import { TIMEZONE_LABELS, type MapsTimezoneKey } from '../../utils/timezone';
import { NormalizeUsState, StateDisplayName } from '../../utils/location';
import { SORT_OPTIONS, type MapsSortKey } from '../../utils/sorting';

type ActiveChip = { label: string; remove: () => void };

/**
 * Results toolbar (STN client round 2026-08): one "Filter" popover holding
 * three multi-select groups — FILTER BY (attribute toggles), TIME ZONE and
 * US STATE (facet rows with live counts) — plus the Sort popover, removable
 * active-filter chips, and a right-aligned export slot.
 *
 * Facet counts are recomputed only when inputs change or a selection is made
 * (ngOnChanges / emit) — never per change-detection cycle (a bound getter
 * over N candidates would run thousands of times).
 *
 * Replaced the 7-15 "exactly the Location fieldset" surface — the regression
 * suite was amended in the same change. The honest-blanks policy holds: every
 * group's predicate fires only on positively-known values (utils/filtering.ts).
 */
@Component({
    selector: 'maps-filter-bar',
    standalone: false,
    templateUrl: './filter-bar.component.html',
    styleUrl: './filter-bar.component.scss',
})
export class FilterBarComponent implements OnChanges {
    @Input() filters: MapsFilterState = EmptyFilterState();
    @Input() sortKey: MapsSortKey = 'bestMatch';
    /** Unfiltered result set — facet counts derive from it. */
    @Input() results: MapsCandidate[] = [];
    /** The position's state from brief.officeLocation.region; when it names a
     *  US state, that state's facet row is tagged "office state". */
    @Input() positionState: string | null = null;

    @Output() filtersChange = new EventEmitter<MapsFilterState>();
    @Output() sortKeyChange = new EventEmitter<MapsSortKey>();

    readonly sortOptions = SORT_OPTIONS;
    readonly attributeOptions: { key: MapsFilterAttribute; label: string }[] = (
        ['hasLinkedIn', 'verifiedHistory', 'usBased'] as const
    ).map((key) => ({ key, label: ATTRIBUTE_LABELS[key] }));

    /** Which popover is open, if any. */
    openMenu: 'filter' | 'sort' | null = null;

    facets: FacetCounts = { timezones: [], states: [] };

    constructor(private readonly host: ElementRef<HTMLElement>) {}

    ngOnChanges(): void {
        this.recomputeFacets();
    }

    // -------------------------------------------------------------- popovers

    ToggleMenu(menu: 'filter' | 'sort'): void {
        this.openMenu = this.openMenu === menu ? null : menu;
    }

    @HostListener('document:click', ['$event'])
    OnDocumentClick(event: MouseEvent): void {
        if (!this.host.nativeElement.contains(event.target as Node)) {
            this.openMenu = null;
        }
    }

    @HostListener('document:keydown.escape')
    OnEscape(): void {
        this.openMenu = null;
    }

    // --------------------------------------------------------------- labels

    get sortLabel(): string {
        return this.sortOptions.find((o) => o.value === this.sortKey)?.label ?? 'Best Match';
    }

    get activeFilterCount(): number {
        return CountActiveFilters(this.filters);
    }

    /** Office-state USPS code when the brief names a recognizable US state. */
    get officeStateCode(): string | null {
        if (!this.positionState) return null;
        const code = NormalizeUsState(this.positionState);
        return StateDisplayName(code) !== null ? code : null;
    }

    /** Selected filters, flattened into removable chips for the bar. */
    get activeChips(): ActiveChip[] {
        const chips: ActiveChip[] = [];
        for (const attr of this.filters.attributes) {
            chips.push({ label: ATTRIBUTE_LABELS[attr], remove: () => this.ToggleAttribute(attr, false) });
        }
        for (const zone of this.filters.timezones) {
            chips.push({ label: TIMEZONE_LABELS[zone], remove: () => this.ToggleTimezone(zone, false) });
        }
        for (const code of this.filters.states) {
            chips.push({
                label: `${StateDisplayName(code) ?? code.toUpperCase()} (${code.toUpperCase()})`,
                remove: () => this.ToggleState(code, false),
            });
        }
        return chips;
    }

    // --------------------------------------------------------------- toggles

    IsAttributeOn(key: MapsFilterAttribute): boolean {
        return this.filters.attributes.has(key);
    }

    IsTimezoneOn(key: MapsTimezoneKey): boolean {
        return this.filters.timezones.has(key);
    }

    IsStateOn(code: string): boolean {
        return this.filters.states.has(code);
    }

    ToggleAttribute(key: MapsFilterAttribute, checked: boolean): void {
        const attributes = new Set(this.filters.attributes);
        if (checked) attributes.add(key);
        else attributes.delete(key);
        this.emit({ ...this.filters, attributes });
    }

    ToggleTimezone(key: MapsTimezoneKey, checked: boolean): void {
        const timezones = new Set(this.filters.timezones);
        if (checked) timezones.add(key);
        else timezones.delete(key);
        this.emit({ ...this.filters, timezones });
    }

    ToggleState(code: string, checked: boolean): void {
        const states = new Set(this.filters.states);
        if (checked) states.add(code);
        else states.delete(code);
        this.emit({ ...this.filters, states });
    }

    ClearFilters(): void {
        this.emit(EmptyFilterState());
    }

    SelectSort(value: MapsSortKey): void {
        this.sortKey = value;
        this.sortKeyChange.emit(value);
        this.openMenu = null;
    }

    private emit(next: MapsFilterState): void {
        this.filters = next;
        this.recomputeFacets();
        this.filtersChange.emit(next);
    }

    private recomputeFacets(): void {
        this.facets = ComputeFacetCounts(this.results, this.filters, this.officeStateCode);
    }
}
